export function useStorySegments({
  story,
  setStory,
  setStep,
  setSelectedSegmentIndex,
  showMessage,
  tx,
}) {
  const updateSegment = (index, patch) => {
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      segs[index] = { ...segs[index], ...patch }
      return { ...prev, segments: segs }
    })
  }

  const addSegment = () => {
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      segs.push({
        text: '',
        image: '',
        extraImages: '',
        audio_url: '',
        duration_ms: 3000,
        frame_duration_ms: 500,
      })
      return { ...prev, segments: segs }
    })
    setSelectedSegmentIndex((story?.segments?.length || 0))
  }

  const removeSegment = (index) => {
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      segs.splice(index, 1)
      return { ...prev, segments: segs }
    })
    setSelectedSegmentIndex((prev) => Math.max(0, Math.min(prev, (story?.segments?.length || 1) - 2)))
  }

  const moveSegment = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= (story?.segments?.length || 0)) return
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      const [removed] = segs.splice(index, 1)
      segs.splice(newIndex, 0, removed)
      return { ...prev, segments: segs }
    })
    setSelectedSegmentIndex(newIndex)
  }

  const handleSegmentsFromSplitter = (result) => {
    const { source_audio, segments } = result
    setStory((prev) => ({
      ...prev,
      source_audio,
      segments: [...segments],
    }))
    setStep(3)
    setSelectedSegmentIndex(0)
    showMessage(tx({ zh: '已生成 {n} 个段落（已覆盖原段落）', en: '{n} segments generated (replaced previous)' }, { n: segments.length }), 'ok')
  }

  const handleSegmentTimeChange = (index, newStartTime) => {
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      if (index > 0 && index < segs.length) {
        segs[index] = { ...segs[index], start_time: newStartTime }
        segs[index - 1] = { ...segs[index - 1], end_time: newStartTime }
        if (segs[index].end_time !== undefined) {
          segs[index] = { ...segs[index], duration_ms: Math.round((segs[index].end_time - newStartTime) * 1000) }
        }
        if (segs[index - 1].start_time !== undefined) {
          segs[index - 1] = {
            ...segs[index - 1],
            duration_ms: Math.round((newStartTime - segs[index - 1].start_time) * 1000),
          }
        }
      }
      return { ...prev, segments: segs }
    })
  }

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

  const updateSegmentWindow = (index, patch) => {
    setStory((prev) => {
      const segs = [...(prev.segments || [])]
      const current = segs[index]
      if (!current) return prev

      const minGap = 0.5
      const duration = Math.max(0, Number(current.end_time ?? 0) - Number(current.start_time ?? 0))

      const prevSeg = index > 0 ? segs[index - 1] : null
      const nextSeg = index < segs.length - 1 ? segs[index + 1] : null

      const lowerBound = prevSeg?.start_time !== undefined ? Number(prevSeg.start_time) + minGap : 0
      const upperBound = nextSeg?.end_time !== undefined ? Number(nextSeg.end_time) - minGap : Number.POSITIVE_INFINITY

      let newStart = current.start_time !== undefined ? Number(current.start_time) : 0
      let newEnd = current.end_time !== undefined ? Number(current.end_time) : newStart + duration

      if (patch.start_time !== undefined) newStart = Number(patch.start_time)
      if (patch.end_time !== undefined) newEnd = Number(patch.end_time)

      if (Number.isFinite(upperBound)) {
        newStart = clamp(newStart, lowerBound, upperBound - minGap)
        newEnd = clamp(newEnd, newStart + minGap, upperBound)
      } else {
        newStart = Math.max(lowerBound, newStart)
        newEnd = Math.max(newStart + minGap, newEnd)
      }

      segs[index] = {
        ...current,
        start_time: newStart,
        end_time: newEnd,
        duration_ms: Math.round((newEnd - newStart) * 1000),
      }

      if (prevSeg && prevSeg.end_time !== undefined) {
        const prevStart = Number(prevSeg.start_time ?? 0)
        segs[index - 1] = {
          ...prevSeg,
          end_time: newStart,
          duration_ms: Math.round((newStart - prevStart) * 1000),
        }
      }

      if (nextSeg && nextSeg.start_time !== undefined) {
        const nextEnd = Number(nextSeg.end_time ?? newEnd + minGap)
        segs[index + 1] = {
          ...nextSeg,
          start_time: newEnd,
          duration_ms: Math.round((nextEnd - newEnd) * 1000),
        }
      }

      return { ...prev, segments: segs }
    })
  }

  const nudgeSegmentStart = (index, deltaSeconds) => {
    const seg = story?.segments?.[index]
    if (!seg || seg.start_time === undefined) return
    updateSegmentWindow(index, { start_time: Number(seg.start_time) + deltaSeconds })
  }

  const nudgeSegmentEnd = (index, deltaSeconds) => {
    const seg = story?.segments?.[index]
    if (!seg || seg.end_time === undefined) return
    updateSegmentWindow(index, { end_time: Number(seg.end_time) + deltaSeconds })
  }


  return { updateSegment, addSegment, removeSegment, moveSegment,
    handleSegmentsFromSplitter, handleSegmentTimeChange, updateSegmentWindow,
    nudgeSegmentStart, nudgeSegmentEnd }
}
